import { useEffect, useMemo, useState } from "react";
import BannerComp from "../components/BannerComp";
import BlogLoadingState from "../components/BlogLoadingState";
import BlogCard from "../components/BlogCard";
import Seo from "../components/Seo";
import api, { apiError } from "../lib/api";
import RenderToast from "../appcomponents/RenderToast";
import { createBreadcrumbSchema, createWebPageSchema } from "../lib/seo";
import styles from "./Blog.module.css";

const INITIAL_VISIBLE_COUNT = 6;
const LOAD_MORE_COUNT = 3;

const seo = {
  title: "Medical Billing & RCM Blog | PSP RCM Insights",
  description:
    "Browse PSP RCM blog articles covering medical billing, coding accuracy, verification of benefits, denials, AR workflows, and healthcare operations.",
  keywords:
    "medical billing blog, RCM blog, coding accuracy articles, verification of benefits blog, denial management insights, healthcare operations articles",
  schema: [
    createWebPageSchema({
      path: "/blog",
      title: "Medical Billing & RCM Blog",
      description:
        "Browse PSP RCM blog articles covering medical billing, coding accuracy, verification of benefits, denials, AR workflows, and healthcare operations.",
      type: "Blog",
    }),
    createBreadcrumbSchema("/blog", "Medical Billing & RCM Blog"),
  ],
};

export default function Blog() {
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/api/public/blogs.php");
        if (active) setBlogs(data.data?.items || []);
      } catch (err) {
        RenderToast({ type: "error", message: apiError(err) });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const visibleBlogs = useMemo(
    () => blogs.slice(0, visibleCount),
    [blogs, visibleCount],
  );

  const hasMore = visibleCount < blogs.length;

  return (
    <>
      <Seo
        title={seo.title}
        metaTitle={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonicalPath="/blog"
        schema={seo.schema}
      />
      <BannerComp
        heading="Insights That Help Practices Build Stronger Revenue Operations"
        paragraph="Explore practical articles on billing, authorizations, AR, practice workflows, and patient-facing operational strategy."
        BannerBgImg="/pages-banner.webp"
        centered
      />

      <section className={styles.section}>
        <div className="container">
          <div className={styles.intro}>
            <span className={styles.eyebrow}>Blog Library</span>
            <h2>
              Clear operational guidance for practices that want fewer billing
              bottlenecks and better visibility.
            </h2>
            <p>
              Each article is designed to be useful in real workflows, with
              direct guidance your team can apply across revenue cycle
              operations and practice growth.
            </p>
          </div>

          {loading ? (
            <BlogLoadingState
              title="Loading blogs"
              message="Pulling the latest posts and preparing the feed."
            />
          ) : (
            <div className={styles.grid}>
              {visibleBlogs.map((blog) => (
                <BlogCard key={blog.slug} blog={blog} />
              ))}
            </div>
          )}

          {hasMore ? (
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.loadMore}
                onClick={() =>
                  setVisibleCount((count) => count + LOAD_MORE_COUNT)
                }
              >
                Load More Blogs
              </button>
            </div>
          ) : null}
        </div>
      </section>
    </>
  );
}
